import React from 'react';
import { Link } from 'react-router-dom';
import LearnLayout from '../LearnLayout';
import { findTopic, neighbors } from '../topics';
import styles from '../Learn.module.css';

const CalibrationExplainer: React.FC = () => {
  const topic = findTopic('calibration')!;
  const { prev, next } = neighbors(topic.slug);
  return (
    <LearnLayout title={topic.title} tagline={topic.blurb} readTime={topic.readTime} prev={prev} next={next}>
      <p>
        Most AI trading products tell you the model is confident. Almost none tell you whether the confidence number
        means anything. The <Link to="/calibration">Calibration dashboard</Link> is SignalDeck's answer to that — a
        running scoreboard that shows what the AI's stated confidence actually translates to in realized win rate. It is
        the single most important page for deciding whether to trust the system.
      </p>

      <h2>How decisions get scored</h2>
      <p>Every call the AI makes (Screener, QuickTrade, or Strategist entry) is logged with:</p>
      <ul>
        <li>The direction (LONG / SHORT / AVOID)</li>
        <li>The stated confidence (0-100)</li>
        <li>The entry price, stop, and take-profit</li>
        <li>The regime, session phase, short interest bucket, and strategy tag at the moment of the call</li>
      </ul>
      <p>
        One hour later, an EventBridge cron fires. It pulls forward 1-hour bars from Alpaca, checks whether the stop or
        take-profit was hit in-direction, and marks each decision <strong>WIN</strong>, <strong>LOSS</strong>, or{' '}
        <strong>NEUTRAL</strong>. No subjective judgment, no cherry-picking, no after-the-fact rationalization.
      </p>

      <h2>What the dashboard shows you</h2>
      <h3>Overall win rate</h3>
      <p>
        Top-of-page stat: <strong>wins / losses / win rate</strong> across everything that's been scored in the lookback
        window. Useful as a baseline, but the bucketed tables below are where the real insight lives.
      </p>

      <h3>By confidence bucket</h3>
      <div className={styles.example}>
        <div className={styles.label}>Example</div>
        <p style={{ margin: 0 }}>
          <code>70-80%</code> bucket: 18 calls, <strong>54%</strong> actual win rate ⚠ OVERCONFIDENT — discount by
          ~24pts
          <br />
          <code>80-90%</code> bucket: 12 calls, <strong>68%</strong> actual win rate ✓ well-calibrated
        </p>
      </div>
      <p>
        This is the headline. If the AI says 75% confidence and the bucket historically hits 54%, the model is
        overconfident by ~20 points <em>on your setup mix</em>. You can — and should — use that to discount your own
        conviction when you act on its output.
      </p>

      <h3>By session phase</h3>
      <p>
        Which part of the trading day is your system strong in, and which is it weak in? LUNCH-hour calls consistently
        underperforming? That's a prompt-tuning target — and until it's tuned, you know to be more selective in that
        window.
      </p>

      <h3>By regime</h3>
      <p>
        Does the AI work better in trend-up markets than in chop? Most do. This table tells you how much better, so you
        can size accordingly when the regime shifts against your historical strong spot.
      </p>

      <h3>By strategy</h3>
      <p>
        For Strategist-sourced decisions: RSI_EXTREME, VWAP_RECLAIM, MACD_CROSSOVER, etc. — each scored independently.
        Lets you see which strategies are carrying the win rate and which are dragging it down.
      </p>

      <h3>By short interest bucket</h3>
      <p>
        Does the AI do better on clean-float names than on high-SI squeeze candidates? Different users' results will
        differ here — worth knowing which tape you're actually good at.
      </p>

      <h2>Community baseline vs your data</h2>
      <div className={styles.callout}>
        <strong>The first 10 decisions:</strong> the page shows aggregate stats across all SignalDeck users instead of
        yours, because 3 wins and 2 losses on your own account is not statistics — it's noise. We flag this clearly at
        the top so you know you're looking at community data.
        <br />
        <br />
        <strong>Once you have 10+ personal scored decisions:</strong> your own data takes over. You'll see a "personal
        history" footer instead of "community aggregate."
      </div>

      <h2>How it feeds back into the AI</h2>
      <p>
        The calibration stats don't just sit on the dashboard. They're injected into every subsequent Screener,
        QuickTrade, and Strategist prompt as a text block that looks roughly like:
      </p>
      <div className={styles.example}>
        <div className={styles.label}>Injected into the prompt</div>
        <p style={{ margin: 0, fontFamily: "'SF Mono', 'Menlo', monospace", fontSize: 12 }}>
          ## Recent Calibration (last 30d, YOUR past Claude calls scored vs actual price action)
          <br />
          - Overall: 42-31 across 92 scored calls (57.5% win rate). Use this as your baseline, not a generic prior.
          <br />
          - 70-80%: 54% actual win rate on 18 calls ⚠ OVERCONFIDENT — discount confidence here by ~24pts
          <br />- Weak session: LUNCH win rate only 38% (8 calls) — be more selective in this window.
        </p>
      </div>
      <p>
        The model sees its own track record inside the same prompt it uses to make the next call. Self-correcting
        feedback — the thing that separates "AI that feels smart" from "AI that actually works."
      </p>

      <h2>Sample-size caveat</h2>
      <div className={styles.calloutWarn}>
        <strong>N &lt; 20 per bucket is noise.</strong> Don't look at a "90+ confidence bucket: 100% win rate, 3 calls"
        and conclude anything. The whole point of this system is to let signal emerge over a statistically meaningful
        sample. Give it at least 2-3 trading weeks before reading any bucket seriously.
      </div>

      <h2>The "Run Scorer" button</h2>
      <p>
        The EventBridge cron runs every 30 minutes automatically. The button exists for the cases where you just
        finished a fresh QuickTrade and want to see the outcome mark the moment it matures (1h post-entry). It is
        idempotent — you can't accidentally double-score anything.
      </p>
    </LearnLayout>
  );
};

export default CalibrationExplainer;
